import { getApi, patchApi, postApi, putApi } from "../helpers/auth_helpers";
import type {
  ApplyResult,
  ApplyState,
  CareEnvelope,
  CareSections,
  CareTemplate,
  CareText,
  ProductCare,
} from "./careTypes";

const base = () => `${(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "")}/care`;

async function request<T>(live: () => Promise<any>): Promise<CareEnvelope<T>> {
  try {
    const res = await live();
    if (res && typeof res.success === "boolean") return res;
  } catch {
    // Network or invalid response: never pretend a save worked.
  }
  return { success: false, code: "NETWORK_UNAVAILABLE", message: "Care could not be loaded or saved. Check your connection and try again." };
}

export const listCareTemplates = (q: { status?: "ACTIVE" | "ARCHIVED" | "ALL"; source?: "GLOBAL" | "STUDIO"; search?: string } = {}) => {
  const params = new URLSearchParams();
  if (q.status) params.set("status", q.status);
  if (q.source) params.set("source", q.source);
  if (q.search) params.set("search", q.search);
  const qs = params.toString();
  return request<CareTemplate[]>(() => getApi(`${base()}/templates${qs ? `?${qs}` : ""}`));
};

export const getCareTemplate = (id: string) =>
  request<CareTemplate>(() => getApi(`${base()}/templates/${encodeURIComponent(id)}`));

export const createCareTemplate = (body: { name: string; sections: CareSections }) =>
  request<CareTemplate>(() => postApi(`${base()}/templates`, body));

export const updateCareTemplate = (id: string, body: { name?: string; sections?: CareSections; expectedVersion: number }) =>
  request<CareTemplate>(() => patchApi(`${base()}/templates/${encodeURIComponent(id)}`, body));

export const setCareTemplateArchived = (id: string, archived: boolean, expectedVersion: number) =>
  request<CareTemplate>(() =>
    postApi(`${base()}/templates/${encodeURIComponent(id)}/${archived ? "archive" : "restore"}`, { expectedVersion })
  );

export const duplicateCareTemplate = (id: string, name?: string) =>
  request<CareTemplate>(() => postApi(`${base()}/templates/${encodeURIComponent(id)}/duplicate`, name ? { name } : {}));

export const careApplyStatus = (id: string, productUniqueIds: string[]) =>
  request<Record<string, { state: ApplyState; templateName?: string }>>(() =>
    postApi(`${base()}/templates/${encodeURIComponent(id)}/apply-status`, { productUniqueIds })
  );

export const applyCareTemplate = (id: string, productUniqueIds: string[], expectedTemplateVersion: number) =>
  request<{ applied: number; results: ApplyResult[] }>(() =>
    postApi(`${base()}/templates/${encodeURIComponent(id)}/apply`, { productUniqueIds, expectedTemplateVersion })
  );

export const getProductCare = (productUniqueId: string) =>
  request<ProductCare>(() => getApi(`${base()}/products/${encodeURIComponent(productUniqueId)}`));

export const saveProductCare = (
  productUniqueId: string,
  body: {
    templateId: string;
    expectedTemplateVersion: number | null;
    expectedProfileVersion: number | null;
    overrides: CareSections;
    additionalNotes: CareText | null;
  }
) => request<ProductCare>(() => putApi(`${base()}/products/${encodeURIComponent(productUniqueId)}`, body));

export const applyLatestProductCare = (productUniqueId: string, expectedVersion: number) =>
  request<ProductCare>(() =>
    postApi(`${base()}/products/${encodeURIComponent(productUniqueId)}/apply-latest-template`, { expectedVersion })
  );

export const removeProductCare = (productUniqueId: string, expectedVersion: number) =>
  request<ProductCare>(() => postApi(`${base()}/products/${encodeURIComponent(productUniqueId)}/remove`, { expectedVersion }));
